import PostingCategoryLabel from "@/components/tenancy/PostingCategoryLabel";
import { POSTING_CATEGORIES, formatEUR, formatDate } from "@/lib/constants";
import { PostingActionsMenu, PeriodBadge } from "@/components/tenancy/PostingActionsMenu";

const Chip = ({ cls, children, testId }) => (
  <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium ${cls}`} data-testid={testId}>
    {children}
  </span>
);

function PostingCard({ p, tenancyId, selected, onToggle, onDeleted, onChanged }) {
  const isSoll = p.type === "soll";
  return (
    <div className={`px-3 py-2.5 border-t border-slate-100 ${selected ? "bg-teal-50/60" : ""} ${p.pre_ownership ? "bg-slate-100/70 text-slate-500 italic" : ""}`}
         data-testid={`ledger-card-${p.id}`}>
      <div className="flex items-start gap-2">
        <input type="checkbox" className="mt-1 h-4 w-4 rounded border-slate-300 text-teal-600"
               checked={selected} onChange={() => onToggle(p.id)} data-testid={`ledger-card-select-${p.id}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-[12px] tabular-nums text-slate-500">{formatDate(p.posting_date)}</span>
            <span className={`tabular-nums font-semibold text-sm ${isSoll ? "text-rose-700" : "text-emerald-700"}`}>
              {isSoll ? "−" : "+"}{formatEUR(isSoll ? p.soll : p.haben)}
            </span>
          </div>
          <div className="flex items-start justify-between gap-2 mt-0.5">
            <div className="text-sm font-medium text-slate-900 break-words"><PostingCategoryLabel p={p} /></div>
            <span className={`text-[11px] tabular-nums whitespace-nowrap ${p.saldo < 0 ? "text-rose-700" : "text-slate-500"}`}>
              Saldo {formatEUR(p.saldo)}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-1 mt-1">
            {p.type === "ist" && !p.linked_installment_plan_id && p.category !== "surplus" && (
              <PeriodBadge p={p} tenancyId={tenancyId} onChanged={onChanged} />
            )}
            {p.split_info && <Chip cls="border-slate-200 bg-white text-slate-500">gesplittet</Chip>}
            {p.is_surplus_offset && <Chip cls="border-emerald-300 bg-emerald-50 text-emerald-800">Verrechnung aus Guthaben</Chip>}
            {p.is_auto_settlement && <Chip cls="border-amber-300 bg-amber-100 text-amber-800">AUTO</Chip>}
            {p.pre_ownership && <Chip cls="border-slate-300 bg-slate-100 text-slate-600">Voreigentümer</Chip>}
          </div>
          {p.reference && <div className="text-[12px] text-slate-600 mt-1 break-words">{p.reference}</div>}
          {p.arrears_allocation?.length > 0 && (
            <div className="text-[11px] text-emerald-700 mt-0.5">
              {p.arrears_allocation.map((a, i) => <div key={i}>→ {formatEUR(a.amount)} {POSTING_CATEGORIES[a.category] || a.category} {a.period}</div>)}
            </div>
          )}
        </div>
        <div className="-mr-1">
          <PostingActionsMenu p={p} tenancyId={tenancyId} onDeleted={onDeleted} onChanged={onChanged} />
        </div>
      </div>
    </div>
  );
}

// Mietkonto auf dem Handy: Buchungen als Karten statt breiter Tabelle.
export function LedgerMobileList({ ledger, tenancyId, selected, onToggle, onSelectAll, onClear, onDelete, onChanged }) {
  const postings = ledger?.postings || [];
  const allSel = postings.length > 0 && selected.size === postings.length;
  return (
    <div className="md:hidden" data-testid="ledger-mobile-list">
      <label className="flex items-center gap-2 px-3 py-2 bg-slate-50 text-xs text-slate-600">
        <input type="checkbox" className="h-4 w-4 rounded border-slate-300 text-teal-600" checked={allSel}
               onChange={(e) => (e.target.checked ? onSelectAll() : onClear())} data-testid="ledger-mobile-select-all" />
        Alle auswählen · {postings.length} Buchungen
      </label>
      {postings.length === 0 && <div className="p-6 text-center text-slate-500 text-sm">Keine Buchungen vorhanden.</div>}
      {postings.map((p) => (
        <PostingCard key={p.id} p={p} tenancyId={tenancyId} selected={selected.has(p.id)} onToggle={onToggle}
                     onDeleted={() => onDelete(p.id)} onChanged={onChanged} />
      ))}
      {postings.length > 0 && (
        <div className="grid grid-cols-3 gap-2 px-3 py-2.5 bg-slate-50 border-t border-slate-200 text-[12px]" data-testid="ledger-mobile-sum">
          <div><div className="text-slate-500">Soll</div><div className="font-semibold tabular-nums text-rose-700">{formatEUR(ledger.soll)}</div></div>
          <div><div className="text-slate-500">Haben</div><div className="font-semibold tabular-nums text-emerald-700">{formatEUR(ledger.ist)}</div></div>
          <div className="text-right"><div className="text-slate-500">Saldo</div>
            <div className={`font-bold tabular-nums ${ledger.balance < 0 ? "text-rose-700" : "text-emerald-700"}`}>{formatEUR(ledger.balance)}</div></div>
        </div>
      )}
    </div>
  );
}
